import {
    CallParticipantListParticipant,
    ParticipantItem,
    ParticipantList,
    ParticipantListParticipant,
} from '@azure/communication-react';
import {
    FocusZone,
    Icon,
    IContextualMenuItem,
    IRawStyle,
    mergeStyles,
    PersonaPresence,
    Stack,
    useTheme,
} from '@fluentui/react';
import React from 'react';
import {
    participantListContainerStyle,
    participantListItemSeparatorStyles,
    participantListMobileStyle,
    participantListStack,
    participantListStyle,
} from '../styles/ParticipantListStyles';
import { createMenuItems, CreateMenuItemsCallbacks } from './util/createMenuItems';
import { CallParticipantListActions } from './CallParticipantListActions';
import {
    MicrophoneIcon,
    NotificationIcon,
    pcpBluePalette,
    RaisedHandIcon,
    selectIsMobile,
    usePcpLiveLessonContext,
} from 'live-lesson/shared/feature-call-ui-react';
import { isParticipantPrioritized, sortParticipantsByPriority } from './util/participantListUtils';

const headlineStyles = mergeStyles({
    color: pcpBluePalette.foregroundDark,
    paddingLeft: '20px',
    fontWeight: 700,
});

const iconStyles = mergeStyles({
    display: 'flex',
    lineHeight: 0,
    alignItems: 'center'
});

const raisedHandIconStyles = {
    root: {
        paddingRight: "1px",    //moving child with "position: static"
        '--raise-hand-icon-size': '24px',
    } as IRawStyle,
    container: {
        '--raise-hand-icon-container-size': '16px',
    } as IRawStyle
};

const notificationIconStyles = {
    root: {
        transform: "translateX(-50%) translateY(-50%)"
    }
};


type CallParticipantListProps = {
    participants: CallParticipantListParticipant[];
    myUserId: string;
    menuItemCallbacks: CreateMenuItemsCallbacks;
};


export const CallParticipantList = (props: CallParticipantListProps): JSX.Element => {
    const theme = useTheme();
    const { participants, myUserId, menuItemCallbacks } = props;
    const isMobile = usePcpLiveLessonContext(selectIsMobile);

    const participantsOrderedByPriority: CallParticipantListParticipant[] = participants
        .filter(p => p.userId !== myUserId) // exclude myself
        .sort(sortParticipantsByPriority);

    const onRenderParticipant = (participant: ParticipantListParticipant): JSX.Element => {
        const participantIndex = participantsOrderedByPriority.map((p) => p.userId).indexOf(participant.userId);

        const callingParticipant = participantsOrderedByPriority[participantIndex] as CallParticipantListParticipant;
        const prevParticipant = participantsOrderedByPriority[participantIndex - 1] as CallParticipantListParticipant | undefined;

        const isCurrentParticipantNotPrioritized = !isParticipantPrioritized(callingParticipant);
        const isPrevParticipantPrioritized = prevParticipant && isParticipantPrioritized(prevParticipant);

        let presence: PersonaPresence | undefined = undefined;
        if (callingParticipant) {
            if (callingParticipant.state === 'Connected') {
                presence = PersonaPresence.online;
            } else if (callingParticipant.state === 'Idle') {
                presence = PersonaPresence.away;
            } else if (callingParticipant.state === 'Connecting') {
                presence = PersonaPresence.offline;
            }

            // NOTE: there are additional states and additional presence states, but they seem to have no effect
        }

        const menuItems: IContextualMenuItem[] = createMenuItems(
            menuItemCallbacks,
            callingParticipant,
            myUserId
        );

        const onRenderIcon =
            !!callingParticipant
                ? () => (
                    <Stack horizontal={true} verticalAlign="center">
                        {
                            callingParticipant.raisedHand && (
                                <RaisedHandIcon styles={raisedHandIconStyles}>
                                    {callingParticipant.raisedHand.raisedHandOrderPosition && (
                                        <NotificationIcon
                                            chatMessagesCount={callingParticipant.raisedHand.raisedHandOrderPosition}
                                            label={'Hand erhoben'}
                                            size={'xsmall'}
                                            horizontalAlign={'left'}
                                            styles={notificationIconStyles}
                                        />
                                    )}
                                </RaisedHandIcon>
                            )
                        }
                        {callingParticipant.isScreenSharing && (
                            <Icon
                                iconName="ParticipantItemScreenShareStart"
                                className={iconStyles}
                                ariaLabel="Teilen"
                            />
                        )}
                        <MicrophoneIcon muted={callingParticipant.isMuted} speaking={callingParticipant.isSpeaking} size={48} />
                    </Stack>
                )
                : () => null;

        // see https://azure.github.io/communication-ui-library/?path=/docs/ui-components-participantitem--participant-item
        if (participant.displayName) {
            return (
                <span key={participant.userId}>
                    {/*<div>curUserId = {callingParticipant.userId}</div>*/}
                    {/*<div>prevUserId = {participants[participantIndex - 1]?.userId}</div>*/}

                    {/*<div>isCurPrio = {JSON.stringify(!isCurrentParticipantNotPrioritized)}</div>*/}
                    {/*<div>isPrevPrio = {JSON.stringify(isPrevParticipantPrioritized)}</div>*/}

                    {/*<div>isMuted = {JSON.stringify(callingParticipant.isMuted)}</div>*/}
                    {/*<div>isSpeaking = {JSON.stringify(callingParticipant.isSpeaking)}</div>*/}
                    {/*<div>raisedHand = {JSON.stringify(callingParticipant.raisedHand?.raisedHandOrderPosition)}</div>*/}

                    {/* Show a separator between prioritized and non prioritized participants for better readability */}
                    {isPrevParticipantPrioritized && isCurrentParticipantNotPrioritized && <Separator />}

                    <ParticipantItem
                        displayName={participant.displayName}
                        userId={participant.userId}
                        key={participant.userId}
                        me={participant.userId === myUserId}
                        menuItems={menuItems.length > 0 ? menuItems : undefined}
                        presence={presence}
                        onRenderIcon={onRenderIcon}
                        styles={participantListStyle.participantItemStyles}

                        // TODO: participantState is not yet available in the library
                        // participantState={callingParticipant?.state}
                    />
                </span>
            );
        }
        return <></>;
    };

    return (
        <Stack className={participantListStack}>

            {/* keeping for doc => could be required again later*/}
            {/*<CallParticipantListContextMenu />*/}

            <h4 className={headlineStyles}>in session</h4>

            <FocusZone className={participantListContainerStyle} shouldFocusOnMount={true}>
                <ParticipantList
                    styles={isMobile ? participantListMobileStyle : participantListStyle}
                    participants={participantsOrderedByPriority}
                    myUserId={myUserId}
                     // override list item rendering
                    onRenderParticipant={onRenderParticipant}
                />

                {/* alternative: render the list completely custom? */}
                {/*{participantsOrderedByPriority.map(p => onRenderParticipant(p)) }*/}
            </FocusZone>
            <CallParticipantListActions />
        </Stack>
    );
};

const Separator = (): JSX.Element => (
    <div className={mergeStyles(participantListItemSeparatorStyles)}></div>
);
